import React from "react";
import type { BankAccountAttributes } from "../../../types";
import { DataList, ListColumn, ListAction, Avatar, Icons } from "../../shared";
import { formatCurrency, formatDate } from "../../../utils/formatUtils";

interface BankAccountsListViewProps {
  accounts: BankAccountAttributes[];
  onEdit: (account: BankAccountAttributes) => void;
  onDelete: (id: number) => void;
  loading?: boolean;
}

export const BankAccountsListView: React.FC<BankAccountsListViewProps> = ({
  accounts,
  onEdit,
  onDelete,
  loading = false,
}) => {
  const columns: ListColumn<BankAccountAttributes>[] = [
    {
      key: "name",
      label: "Conta",
      width: "2fr",
      render: (account) => (
        <div className="flex items-center">
          <Avatar text={account.name} size="md" variant="emerald" />
          <div className="ml-3">
            <p className="text-sm font-medium text-gray-900 dark:text-slate-200">{account.name}</p>
          </div>
        </div>
      ),
    },
    {
      key: "openingDate",
      label: "Data de abertura",
      width: "1fr",
      render: (account) => (
        <span className="text-sm text-gray-600 dark:text-slate-400">
          {formatDate(account.openingDate)}
        </span>
      ),
    },
    {
      key: "initialBalance",
      label: "Saldo inicial",
      width: "1fr",
      render: (account) => (
        <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
          {formatCurrency(account.initialBalance)}
        </span>
      ),
    },
    {
      key: "currentBalance",
      label: "Saldo atual",
      width: "1fr",
      render: (account) => (
        <span className="text-sm font-semibold text-gray-800 dark:text-slate-200 tabular-nums">
          {account.currentBalance != null ? formatCurrency(account.currentBalance) : "—"}
        </span>
      ),
    },
  ];

  const actions: ListAction<BankAccountAttributes>[] = [
    {
      label: "Editar",
      icon: <Icons.Edit />,
      onClick: (account) => onEdit(account),
      className:
        "p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200",
      title: "Editar conta",
      disabled: () => loading,
    },
    {
      label: "Excluir",
      icon: <Icons.Delete />,
      onClick: (account) => onDelete(account.id),
      className:
        "p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200",
      title: "Excluir conta",
      disabled: () => loading,
    },
  ];

  return (
    <DataList<BankAccountAttributes>
      data={accounts}
      columns={columns}
      actions={actions}
      keyExtractor={(account) => account.id}
      emptyMessage="Nenhuma conta encontrada"
      loading={loading}
      onRowClick={(account) => onEdit(account)}
    />
  );
};
